import { Component, OnInit, Inject } from '@angular/core';
import { PricesService } from '../../core/services/prices.service';
import { ClientsService } from '../../core/services/clients.service';
import { Price, CreatePriceDto, UpdatePriceDto, MealType } from '../../core/models/price.model';
import { Client } from '../../core/models/client.model';
import { CommonModule, NgIf, NgFor } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-prices',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  styles: [`
    .page-header { display: flex; flex-direction: column; gap: 4px; margin-bottom: 16px; }
    .page-title { margin: 0; font-weight: 600; letter-spacing: 0.2px; color: #1F2937; }
    .page-subtitle { margin: 0; color: rgba(0,0,0,0.6); font-size: 13px; }
    .header-actions { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
    .card { border: 1px solid rgba(0,0,0,0.08); border-radius: 8px; overflow: hidden; }
    .card-body { padding: 16px; }
    table { width: 100%; }
  `],
  template: `
    <div class="page-header">
      <h2 class="page-title">{{ 'prices.title' | translate }}</h2>
      <p class="page-subtitle">{{ 'prices.subtitle' | translate }}</p>
    </div>

    <div class="header-actions">
      <span></span>
      <button mat-raised-button color="primary" class="create-button" (click)="openCreate()">
        <mat-icon>add</mat-icon>
        {{ 'prices.create' | translate }}
      </button>
    </div>

    <mat-card class="card">
      <div class="card-body" *ngIf="loading" style="display: flex; justify-content: center; padding: 8px 0;">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
      <div class="card-body" *ngIf="!loading && !data.length">{{ 'prices.noData' | translate }}</div>

      <table mat-table [dataSource]="data" *ngIf="!loading && data.length">
        <ng-container matColumnDef="client">
          <th mat-header-cell *matHeaderCellDef>{{ 'common.client' | translate }}</th>
          <td mat-cell *matCellDef="let p">{{ clientNameFor(p) }}</td>
        </ng-container>
        <ng-container matColumnDef="mealType">
          <th mat-header-cell *matHeaderCellDef>{{ 'prices.mealType' | translate }}</th>
          <td mat-cell *matCellDef="let p">{{ formatMealType(p.mealType) }}</td>
        </ng-container>
        <ng-container matColumnDef="price">
          <th mat-header-cell *matHeaderCellDef>{{ 'prices.price' | translate }}</th>
          <td mat-cell *matCellDef="let p">{{ p.priceAmount | number:'1.2-2' }}</td>
        </ng-container>
        <ng-container matColumnDef="period">
          <th mat-header-cell *matHeaderCellDef>{{ 'prices.period' | translate }}</th>
          <td mat-cell *matCellDef="let p">{{ p.validFrom | date:'mediumDate' }} - {{ p.validTo ? (p.validTo | date:'mediumDate') : '∞' }}</td>
        </ng-container>
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="table-actions">{{ 'common.actions' | translate }}</th>
          <td mat-cell *matCellDef="let p" class="table-actions">
            <button mat-icon-button class="action-button edit-button" (click)="openEdit(p)"><mat-icon svgIcon="edit-custom"></mat-icon></button>
            <button mat-icon-button class="action-button delete-button" (click)="confirmDelete(p)"><mat-icon svgIcon="delete-custom"></mat-icon></button>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="cols"></tr>
        <tr mat-row *matRowDef="let row; columns: cols;"></tr>
      </table>
    </mat-card>
  `
})
export class PricesComponent implements OnInit {
  constructor(
    private prices: PricesService,
    private clients: ClientsService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private t: TranslateService
  ) {}

  cols = ['client', 'mealType', 'price', 'period', 'actions'];
  data: Price[] = [];
  loading = false;

  ngOnInit(): void {
    this.reload();
  }

  reload() {
    this.loading = true;
    this.prices.getAll().subscribe({
      next: (list) => {
        this.data = list;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snack.open(this.t.instant('common.loadError'), this.t.instant('common.close'), { duration: 3000 });
      }
    });
  }

  clientNameFor(p: Price) { return p.clientName || (p.clientId ? '' : this.t.instant('prices.default')); }

  formatMealType(mt: MealType): string {
    if (mt === 0 || mt === 'Breakfast') return 'Breakfast';
    if (mt === 1 || mt === 'Lunch') return 'Lunch';
    if (mt === 2 || mt === 'Dinner') return 'Dinner';
    if (mt === 3 || mt === 'LunchPackage') return 'LunchPackage';
    return String(mt);
  }

  openCreate() {
    const ref = this.dialog.open(PriceDialogComponent, {
      data: { price: null, allPrices: this.data },
      width: '520px'
    });
    ref.afterClosed().subscribe((dto?: CreatePriceDto) => {
      if (!dto) return;
      this.prices.create(dto).subscribe({
        next: () => { this.snack.open(this.t.instant('prices.created'), this.t.instant('common.close'), { duration: 2000 }); this.reload(); },
        error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.saveError'), this.t.instant('common.close'), { duration: 3000 })
      });
    });
  }

  openEdit(price: Price) {
    const ref = this.dialog.open(PriceDialogComponent, {
      data: { price: price, allPrices: this.data },
      width: '520px'
    });
    ref.afterClosed().subscribe((dto?: UpdatePriceDto) => {
      if (!dto) return;
      this.prices.update(price.id, dto).subscribe({
        next: () => { this.snack.open(this.t.instant('prices.updated'), this.t.instant('common.close'), { duration: 2000 }); this.reload(); },
        error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.saveError'), this.t.instant('common.close'), { duration: 3000 })
      });
    });
  }

  confirmDelete(price: Price) {
    const yes = confirm(this.t.instant('prices.confirmDelete'));
    if (!yes) return;
    this.prices.delete(price.id).subscribe({
      next: () => { this.snack.open(this.t.instant('prices.deleted'), this.t.instant('common.close'), { duration: 2000 }); this.reload(); },
      error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.deleteError'), this.t.instant('common.close'), { duration: 3000 })
    });
  }
}

@Component({
  selector: 'app-price-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatDialogModule,
    TranslateModule,
    NgIf,
    NgFor
  ],
  template: `
    <h2 mat-dialog-title>{{ data ? ('prices.edit' | translate) : ('prices.create' | translate) }}</h2>
    <form [formGroup]="form" (ngSubmit)="save()" style="padding: 0 24px 16px 24px; display:block; width:460px; max-width:100%;">
      <mat-form-field style="width:100%" appearance="outline">
        <mat-label>{{ 'common.client' | translate }}</mat-label>
        <mat-select formControlName="clientId">
          <mat-option [value]="null">{{ 'prices.default' | translate }}</mat-option>
          <mat-option *ngFor="let c of clientsList" [value]="c.id">{{ c.name }}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field style="width:100%" appearance="outline">
        <mat-label>{{ 'prices.mealType' | translate }}</mat-label>
        <mat-select formControlName="mealType" required>
          <mat-option *ngFor="let mt of mealTypes" [value]="mt">{{ mt }}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field style="width:100%" appearance="outline">
        <mat-label>{{ 'prices.price' | translate }}</mat-label>
        <input matInput type="number" step="0.01" formControlName="priceAmount" required>
      </mat-form-field>
      <div style="display:flex; gap:8px;">
        <mat-form-field style="flex:1" appearance="outline">
          <mat-label>{{ 'prices.validFrom' | translate }}</mat-label>
          <input matInput [matDatepicker]="from" formControlName="validFrom" required>
          <mat-datepicker-toggle matSuffix [for]="from"></mat-datepicker-toggle>
          <mat-datepicker #from></mat-datepicker>
        </mat-form-field>
        <mat-form-field style="flex:1" appearance="outline">
          <mat-label>{{ 'prices.validTo' | translate }}</mat-label>
          <input matInput [matDatepicker]="to" formControlName="validTo">
          <mat-datepicker-toggle matSuffix [for]="to"></mat-datepicker-toggle>
          <mat-datepicker #to></mat-datepicker>
        </mat-form-field>
      </div>
      <div *ngIf="overlapError" style="color: #f44336; font-size: 12px; margin-top: -8px; margin-bottom: 8px; padding: 8px; background: #ffebee; border-radius: 4px;">
        <mat-icon style="font-size: 16px; width: 16px; height: 16px; vertical-align: text-bottom; margin-right: 4px;">error</mat-icon>
        {{ overlapError }}
      </div>
      <div style="margin-top:12px; display:flex; gap:8px; justify-content:flex-end;">
        <button mat-stroked-button mat-dialog-close>{{ 'common.cancel' | translate }}</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="form.invalid || overlapError">{{ 'common.save' | translate }}</button>
      </div>
      <div *ngIf="loading" style="display:flex; justify-content:center; padding-top:8px;">
        <mat-spinner diameter="24"></mat-spinner>
      </div>
    </form>
  `
})
export class PriceDialogComponent implements OnInit {
  mealTypes: MealType[] = ['Breakfast', 'Lunch', 'Dinner', 'LunchPackage'];
  clientsList: Client[] = [];
  loading = false;
  form = this.fb.group({
    clientId: [null as string | null],
    mealType: [null as MealType | null, [Validators.required]],
    priceAmount: [0, [Validators.required, Validators.min(0)]],
    validFrom: [new Date(), [Validators.required]],
    validTo: [null as Date | null]
  });

  overlapError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<PriceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { price: Price | null; allPrices: Price[] },
    private clients: ClientsService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.clients.getAll().subscribe({
      next: list => { this.clientsList = list; this.loading = false; },
      error: () => { this.loading = false; }
    });
    if (this.data.price) {
      this.form.patchValue({
        clientId: this.data.price.clientId ?? null,
        mealType: this.data.price.mealType,
        priceAmount: this.data.price.priceAmount,
        validFrom: new Date(this.data.price.validFrom),
        validTo: this.data.price.validTo ? new Date(this.data.price.validTo) : null
      });
    }

    // Watch for changes and validate in real-time
    this.form.valueChanges.subscribe(() => {
      this.validateOverlap();
    });
  }

  validateOverlap() {
    const v = this.form.value;

    // Only validate if we have the required fields
    if (!v.mealType || !v.validFrom) {
      this.overlapError = null;
      return;
    }

    const validFromDate = v.validFrom as Date;
    const validFromUTC = new Date(Date.UTC(validFromDate.getFullYear(), validFromDate.getMonth(), validFromDate.getDate(), 12, 0, 0));

    let validToUTC = null;
    if (v.validTo) {
      const validToDate = v.validTo as Date;
      validToUTC = new Date(Date.UTC(validToDate.getFullYear(), validToDate.getMonth(), validToDate.getDate(), 12, 0, 0));
    }

    const overlap = this.checkPriceOverlap(
      v.clientId ?? null,
      v.mealType!,
      validFromUTC,
      validToUTC
    );

    if (overlap) {
      this.overlapError = `Price overlap! Existing price: ${new Date(overlap.validFrom).toLocaleDateString()} - ${overlap.validTo ? new Date(overlap.validTo).toLocaleDateString() : '∞'}`;
    } else {
      this.overlapError = null;
    }
  }

  private checkPriceOverlap(clientId: string | null, mealType: MealType, validFrom: Date, validTo: Date | null): Price | null {
    const relevantPrices = this.data.allPrices.filter(p => {
      if (this.data.price && p.id === this.data.price.id) return false;
      const clientMatch = (p.clientId === clientId) || (p.clientId === null && clientId === null);
      const mealTypeMatch = this.normalizeMealType(p.mealType) === this.normalizeMealType(mealType);
      return clientMatch && mealTypeMatch;
    });

    for (const existingPrice of relevantPrices) {
      const existingFrom = new Date(existingPrice.validFrom);
      const existingTo = existingPrice.validTo ? new Date(existingPrice.validTo) : null;
      if (this.periodsOverlap(validFrom, validTo, existingFrom, existingTo)) {
        return existingPrice;
      }
    }
    return null;
  }

  private periodsOverlap(start1: Date, end1: Date | null, start2: Date, end2: Date | null): boolean {
    const end1Time = end1 ? end1.getTime() : Infinity;
    const end2Time = end2 ? end2.getTime() : Infinity;
    const start1Time = start1.getTime();
    const start2Time = start2.getTime();
    return start1Time <= end2Time && start2Time <= end1Time;
  }

  private normalizeMealType(mt: any): number {
    if (typeof mt === 'number') return mt;
    if (mt === 'Breakfast') return 0;
    if (mt === 'Lunch') return 1;
    if (mt === 'Dinner') return 2;
    if (mt === 'LunchPackage') return 3;
    return -1;
  }

  save() {
    if (this.form.invalid || this.overlapError) return;
    const v = this.form.value;

    // Validate date range
    if (v.validTo && (v.validFrom as Date) > (v.validTo as Date)) {
      alert('Valid From date must be before Valid To date');
      return;
    }

    // Convert dates to UTC at noon to avoid timezone issues
    const validFromDate = v.validFrom as Date;
    const validFromUTC = new Date(Date.UTC(validFromDate.getFullYear(), validFromDate.getMonth(), validFromDate.getDate(), 12, 0, 0));

    let validToUTC = null;
    if (v.validTo) {
      const validToDate = v.validTo as Date;
      validToUTC = new Date(Date.UTC(validToDate.getFullYear(), validToDate.getMonth(), validToDate.getDate(), 12, 0, 0));
    }

    const dto: CreatePriceDto = {
      clientId: v.clientId ?? null,
      mealType: ((): any => {
        const mt = v.mealType as any;
        if (typeof mt === 'number') return mt;
        if (mt === 'Breakfast') return 0;
        if (mt === 'Lunch') return 1;
        if (mt === 'Dinner') return 2;
        if (mt === 'LunchPackage') return 3;
        return mt;
      })(),
      priceAmount: Number(v.priceAmount),
      validFrom: validFromUTC.toISOString(),
      validTo: validToUTC ? validToUTC.toISOString() : null
    } as CreatePriceDto;
    this.dialogRef.close(dto);
  }
}
